import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { RefreshCw, Eye, Download, Filter, LogOut } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ['New', 'Information Requested', 'Under Review', 'Future Match', 'Pending Assessment', 'Waiting on Licensure', 'Closed'];
const REFERRAL_TYPES = ['Family Member', 'Guardian', 'Self Referral', 'Support Coordinator', 'Case Manager', 'CSB', 'Hospital', 'Behavioral Health Provider', 'Other'];
const FUNDING = ['Medicaid Waiver', 'Private Pay', 'State-Funded', 'Unknown / Pending', 'Other'];

const statusColor = {
  'New': 'bg-blue-100 text-blue-800 border-blue-200',
  'Information Requested': 'bg-amber-100 text-amber-800 border-amber-200',
  'Under Review': 'bg-purple-100 text-purple-800 border-purple-200',
  'Future Match': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Pending Assessment': 'bg-cyan-100 text-cyan-800 border-cyan-200',
  'Waiting on Licensure': 'bg-orange-100 text-orange-800 border-orange-200',
  'Closed': 'bg-gray-100 text-gray-700 border-gray-200'
};

const AdminReferralInterest = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', county: '', referralSource: 'all', fundingSource: 'all', dateFrom: '', dateTo: '' });
  const [selected, setSelected] = useState(null);
  const [edit, setEdit] = useState({ status: '', internalNotes: '' });

  const queryString = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.status !== 'all') p.append('status', filters.status);
    if (filters.county) p.append('county', filters.county);
    if (filters.referralSource !== 'all') p.append('referralSource', filters.referralSource);
    if (filters.fundingSource !== 'all') p.append('fundingSource', filters.fundingSource);
    if (filters.dateFrom) p.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) p.append('dateTo', filters.dateTo);
    return p.toString();
  }, [filters]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/referral-interest/list?${queryString()}`, { withCredentials: true });
      setRows(res.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) { navigate('/admin/login', { replace: true }); return; }
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  }, [queryString, navigate]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const saveUpdate = async (id) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/referral-interest/${id}`, edit, { withCredentials: true });
      toast.success('Referral updated');
      setSelected(null);
      fetchRows();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const exportCsv = () => {
    window.open(`${BACKEND_URL}/api/referral-interest/export?${queryString()}`, '_blank');
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Logout should always proceed; surface the failure without blocking the redirect.
      console.warn('Logout request failed; clearing session locally.', err);
    }
    navigate('/admin/login', { replace: true });
  };

  const fmt = (s) => s ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="relative z-10 min-h-screen bg-cream-200 py-12 px-6 overflow-x-hidden">
      <Helmet>
        <title>Referral Admin | Forever Path Living™</title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
      </Helmet>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-4xl text-forest-800 mb-1">Referral Interest & Future Placement</h1>
            <p className="text-forest-700">Manage and review referral interest submissions</p>
          </div>
          <Button onClick={logout} variant="outline" className="border-sage-300 text-forest-700 w-fit" data-testid="admin-logout">
            <LogOut className="w-4 h-4 mr-2" />Sign Out
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-5 bg-cream-100 border border-sage-200 rounded-2xl mb-6" data-testid="admin-filters">
          <div className="flex items-center gap-2 mb-4 text-forest-700"><Filter className="w-4 h-4" /><span className="font-medium text-sm">Filters</span></div>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.referralSource} onValueChange={(v) => setFilters({ ...filters, referralSource: v })}>
              <SelectTrigger data-testid="filter-source"><SelectValue placeholder="Referral Source" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Sources</SelectItem>{REFERRAL_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.fundingSource} onValueChange={(v) => setFilters({ ...filters, fundingSource: v })}>
              <SelectTrigger data-testid="filter-funding"><SelectValue placeholder="Funding" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Funding</SelectItem>{FUNDING.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="filter-county" placeholder="County / City" value={filters.county} onChange={(e) => setFilters({ ...filters, county: e.target.value })} />
            <Input data-testid="filter-datefrom" type="date" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} />
            <Input data-testid="filter-dateto" type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
          </div>
          <div className="flex gap-3 mt-4">
            <Button onClick={fetchRows} variant="outline" className="border-sage-300 text-forest-700" data-testid="admin-refresh"><RefreshCw className="w-4 h-4 mr-2" />Apply / Refresh</Button>
            <Button onClick={exportCsv} className="bg-forest-800 hover:bg-forest-900 text-cream-100" data-testid="admin-export"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
          </div>
        </Card>

        {loading ? (
          <div className="text-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-forest-700 mx-auto" /><p className="mt-4 text-forest-700">Loading…</p></div>
        ) : rows.length === 0 ? (
          <Card className="p-12 text-center bg-cream-100 border border-sage-200 rounded-2xl"><p className="text-forest-700 text-lg">No referral interest submissions found</p></Card>
        ) : (
          <div className="grid gap-4" data-testid="admin-rows">
            {rows.map((r) => (
              <Card key={r.id} className="p-4 sm:p-6 bg-cream-100 border border-sage-200 rounded-2xl">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="font-serif text-xl text-forest-800 break-words">{r.specificIndividual && r.residentName ? r.residentName : r.contactName}</h3>
                      <Badge className={`${statusColor[r.status] || ''} border`}>{r.status}</Badge>
                      {!r.specificIndividual && <span className="text-xs text-forest-700/70">(future / no specific individual)</span>}
                    </div>
                    <div className="grid md:grid-cols-2 gap-2 text-sm [&_span]:break-words">
                      <div><span className="font-semibold text-forest-800">Contact:</span> <span className="text-forest-700 break-all">{r.contactName} ({r.email})</span></div>
                      <div><span className="font-semibold text-forest-800">Referral Type:</span> <span className="text-forest-700">{(r.referralTypes || []).join(', ')}</span></div>
                      <div><span className="font-semibold text-forest-800">County/City:</span> <span className="text-forest-700">{r.countyCity || '—'}</span></div>
                      <div><span className="font-semibold text-forest-800">Funding:</span> <span className="text-forest-700">{r.fundingSource || '—'}</span></div>
                      <div><span className="font-semibold text-forest-800">Timeframe:</span> <span className="text-forest-700">{r.expectedTimeframe || '—'}</span></div>
                      <div><span className="font-semibold text-forest-800">Submitted:</span> <span className="text-forest-700">{fmt(r.dateSubmitted)}</span></div>
                    </div>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-sage-300 text-forest-700" data-testid={`view-${r.id}`}
                        onClick={() => { setSelected(r); setEdit({ status: r.status, internalNotes: r.internalNotes || '' }); }}>
                        <Eye className="w-4 h-4 mr-2" />View
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                      <DialogHeader><DialogTitle className="font-serif text-2xl text-forest-800">Referral Details</DialogTitle></DialogHeader>
                      {selected && (
                        <div className="space-y-4 text-sm">
                          {[
                            ['Referral Type', (selected.referralTypes || []).join(', ')],
                            ['Specific individual?', selected.specificIndividual ? 'Yes' : 'No — future referral'],
                            ['Resident Name', selected.residentName], ['Age', selected.age], ['County / City', selected.countyCity],
                            ['Current Living', selected.currentLivingArrangement], ['Primary Diagnosis', selected.primaryDiagnosis],
                            ['Support Needs', selected.supportNeeds], ['Current Provider', selected.currentProvider],
                            ['Waiver Type', selected.waiverType], ['Funding Source', selected.fundingSource], ['Timeframe', selected.expectedTimeframe],
                            ['About the Person', selected.aboutPerson], ['Contact Name', selected.contactName], ['Relationship', selected.relationship],
                            ['Phone', selected.phone], ['Email', selected.email], ['Preferred Contact', selected.preferredContactMethod],
                            ['Additional Notes', selected.additionalNotes], ['Submitted', fmt(selected.dateSubmitted)]
                          ].filter(([, v]) => v).map(([k, v]) => (
                            <div key={k}><span className="font-semibold text-forest-800">{k}:</span> <span className="text-forest-700 break-all">{v}</span></div>
                          ))}
                          <div className="pt-4 border-t border-sage-200">
                            <Label className="text-forest-800 font-semibold">Update Status</Label>
                            <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                              <SelectTrigger className="mt-2" data-testid="edit-status"><SelectValue /></SelectTrigger>
                              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-forest-800 font-semibold">Internal Notes</Label>
                            <Textarea className="mt-2" rows={4} value={edit.internalNotes} onChange={(e) => setEdit({ ...edit, internalNotes: e.target.value })} data-testid="edit-notes" />
                          </div>
                          <Button onClick={() => saveUpdate(selected.id)} className="w-full bg-forest-800 hover:bg-forest-900 text-cream-100" data-testid="edit-save">Save Changes</Button>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                </div>
                {r.internalNotes && <div className="mt-3 pt-3 border-t border-sage-200 text-sm"><span className="font-semibold text-forest-800">Notes:</span> <span className="text-forest-700">{r.internalNotes}</span></div>}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReferralInterest;
